import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { cancelSessionWithRefund } from '../../utils/stripe';
import { 
  createOrGetConversation,
  subscribeToMessages,
  sendMessage,
  formatMessageTime
} from '../../utils/messaging';
import './SessionDetailView.css';

const SessionDetailView = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [session, setSession] = useState(null);
  const [otherUser, setOtherUser] = useState(null); // Will be provider or practitioner depending on current user's role
  const [loading, setLoading] = useState(true);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [cancellingSession, setCancellingSession] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'practitioner' or 'provider'

  useEffect(() => {
    if (currentUser && sessionId) {
      fetchSessionDetails();
      fetchCurrentUserData();
    }
  }, [currentUser, sessionId]);

  useEffect(() => {
    let unsubscribe = null;
    
    if (conversation) {
      unsubscribe = subscribeToMessages(conversation.id, (updatedMessages) => {
        setMessages(updatedMessages);
      });
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [conversation]);

  const fetchCurrentUserData = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        setCurrentUserData({ id: userDoc.id, ...userDoc.data() });
      }
    } catch (error) {
      console.error('Error fetching current user data:', error);
    }
  };

  const fetchSessionDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch session
      const sessionDoc = await getDoc(doc(db, 'sessions', sessionId));
      
      if (!sessionDoc.exists()) {
        navigate('/practitioner-sessions');
        return;
      }

      const sessionData = { id: sessionDoc.id, ...sessionDoc.data() };
      
      // Determine user's role and verify access
      let userRole = null;
      let otherUserId = null;
      let backPath = null;
      
      if (sessionData.practitionerId === currentUser.uid) {
        userRole = 'practitioner';
        otherUserId = sessionData.providerId;
        backPath = '/practitioner-sessions';
      } else if (sessionData.providerId === currentUser.uid) {
        userRole = 'provider';
        otherUserId = sessionData.practitionerId;
        backPath = '/provider-sessions';
      } else {
        // User doesn't have access to this session
        navigate('/');
        return;
      }

      setUserRole(userRole);
      setSession(sessionData);

      // Fetch other user's details (provider if current user is practitioner, vice versa)
      const otherUserDoc = await getDoc(doc(db, 'users', otherUserId));
      if (otherUserDoc.exists()) {
        const otherUserData = { id: otherUserDoc.id, ...otherUserDoc.data() };
        setOtherUser(otherUserData);
        
        // Fetch or create conversation after we have other user's data
        await fetchConversationWithData(sessionData.practitionerId, sessionData.providerId, otherUserData);
      }
      
    } catch (error) {
      console.error('Error fetching session details:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationWithData = async (practitionerId, providerId, otherUserData) => {
    try {
      // Prepare user data for conversation creation
      const currentUserInfo = currentUserData || { 
        firstName: 'User', 
        lastName: '', 
        userType: userRole || 'user'
      };
      
      const otherUserInfo = otherUserData || { 
        firstName: 'User', 
        lastName: '', 
        userType: userRole === 'practitioner' ? 'provider' : 'practitioner' 
      };
      
      const conversation = await createOrGetConversation(
        practitionerId, 
        providerId, 
        userRole === 'practitioner' ? currentUserInfo : otherUserInfo,
        userRole === 'provider' ? currentUserInfo : otherUserInfo
      );
      
      setConversation(conversation);
    } catch (error) {
      console.error('Error fetching conversation:', error);
    }
  };

  const canCancelSession = (session) => {
    if (!session || !['scheduled', 'confirmed'].includes(session.status)) {
      return false;
    }

    const sessionDateTime = new Date(`${session.scheduledDate}T${session.scheduledTime}`);
    const now = new Date();
    const fourHoursFromNow = new Date(now.getTime() + (4 * 60 * 60 * 1000));
    
    return sessionDateTime > fourHoursFromNow;
  };

  const handleCancelSession = async () => {
    if (!session || !canCancelSession(session)) {
      alert('This session cannot be cancelled. Sessions must be cancelled more than 4 hours in advance.');
      return;
    }

    const sessionDateTime = new Date(`${session.scheduledDate}T${session.scheduledTime}`);
    const confirmMessage = `Are you sure you want to cancel your ${session.sessionType} session scheduled for ${sessionDateTime.toLocaleString()}?\n\nThis will issue a full refund.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setCancellingSession(true);
    try {
      await cancelSessionWithRefund(session.id);
      
      // Refresh session data
      await fetchSessionDetails();
      
      alert('Session cancelled successfully! Your refund will be processed within 5-10 business days.');
    } catch (error) {
      console.error('Error cancelling session:', error);
      let errorMessage = 'Failed to cancel session. ';
      
      if (error.code === 'failed-precondition') {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again or contact support.';
      }
      
      alert(errorMessage);
    } finally {
      setCancellingSession(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !conversation || !currentUser || !currentUserData || !otherUser) {
      return;
    }

    setSending(true);

    try {
      await sendMessage(
        conversation.id,
        currentUser.uid,
        `${currentUserData.firstName} ${currentUserData.lastName}`.trim(),
        otherUser.id,
        newMessage,
        'text'
      );

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#f59e0b';
      case 'confirmed': return '#3b82f6';
      case 'in_progress': return '#059669';
      case 'completed': return '#6b7280';
      case 'cancelled': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'scheduled': return 'Scheduled';
      case 'confirmed': return 'Confirmed';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  const formatScheduledDateTime = (date, time) => {
    if (!date || !time) return 'Not scheduled';
    return `${new Date(date).toLocaleDateString()} at ${time}`;
  };

  if (loading) {
    return <div className="loading">Loading session details...</div>;
  }

  if (!session || !otherUser) {
    return <div className="error">Session not found</div>;
  }

  const backPath = userRole === 'practitioner' ? '/practitioner-sessions' : '/provider-sessions';
  const otherUserTitle = userRole === 'practitioner' ? 'Provider Information' : 'Practitioner Information';
  const otherUserName = `${otherUser.firstName} ${otherUser.lastName}`;

  return (
    <div className="session-detail-view">
      <div className="session-detail-header">
        <button className="back-button" onClick={() => navigate(backPath)}>
          ← Back to Sessions
        </button>
        <h1>Session Details</h1>
      </div>

      <div className="session-detail-content">
        {/* Session Information Panel */}
        <div className="session-info-panel">
          <div className="session-basic-info">
            <div className="session-title">
              <h2>{session.sessionType}</h2>
              <div 
                className="session-status-badge"
                style={{ backgroundColor: getStatusColor(session.status) }}
              >
                {getStatusLabel(session.status)}
              </div>
            </div>
            
            <div className="session-amount">{formatCurrency(session.amount)}</div>
          </div>

          <div className="session-details-grid">
            <div className="detail-item">
              <span className="label">Duration:</span>
              <span className="value">{session.duration} minutes</span>
            </div>
            <div className="detail-item">
              <span className="label">Scheduled:</span>
              <span className="value">{formatScheduledDateTime(session.scheduledDate, session.scheduledTime)}</span>
            </div>
            <div className="detail-item">
              <span className="label">Booked:</span>
              <span className="value">{session.createdAt ? new Date(session.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}</span>
            </div>
            {session.notes && (
              <div className="detail-item notes">
                <span className="label">Notes:</span>
                <span className="value">{session.notes}</span>
              </div>
            )}
            {session.status === 'cancelled' && session.refundAmount && (
              <div className="detail-item">
                <span className="label">Refund:</span>
                <span className="value refund">{formatCurrency(session.refundAmount)} processed</span>
              </div>
            )}
          </div>

          {/* Other User Information */}
          <div className="provider-info-section">
            <h3>{otherUserTitle}</h3>
            <div className="provider-card">
              <div className="provider-avatar">
                {otherUser.profilePicture ? (
                  <img src={otherUser.profilePicture} alt={otherUserName} />
                ) : (
                  <div className="default-avatar">
                    {otherUser.firstName?.[0]}{otherUser.lastName?.[0]}
                  </div>
                )}
              </div>
              <div className="provider-details">
                <h4>{otherUserName}</h4>
                {userRole === 'practitioner' && otherUser.hourlyRate && (
                  <p className="provider-rate">${otherUser.hourlyRate}/hour</p>
                )}
                {userRole === 'provider' && otherUser.userType && (
                  <p className="provider-rate">Practitioner</p>
                )}
                {otherUser.bio && <p className="provider-bio">{otherUser.bio}</p>}
              </div>
            </div>
          </div>

          {/* Session Actions */}
          <div className="session-actions">
            {userRole === 'practitioner' && canCancelSession(session) && (
              <button 
                onClick={handleCancelSession}
                disabled={cancellingSession}
                className="btn-cancel-session"
              >
                {cancellingSession ? 'Cancelling...' : 'Cancel Session'}
              </button>
            )}
            {userRole === 'provider' && (
              <div className="provider-actions">
                <p className="action-note">Session management available on provider sessions page</p>
              </div>
            )}
          </div>
        </div>

        {/* Messaging Panel */}
        <div className="messaging-panel">
          <div className="messaging-header">
            <h3>Conversation with {otherUser.firstName}</h3>
          </div>
          
          <div className="messages-area">
            {messages.length === 0 ? (
              <div className="no-messages">
                <p>No messages yet.</p>
                <p>Start a conversation with {otherUser.firstName}!</p>
              </div>
            ) : (
              messages.map(message => {
                const isOwnMessage = message.senderId === currentUser.uid;
                
                return (
                  <div 
                    key={message.id} 
                    className={`message ${isOwnMessage ? 'own-message' : 'other-message'}`}
                  >
                    <div className="message-content">
                      <p>{message.content}</p>
                      <span className="message-time">
                        {formatMessageTime(message.timestamp)}
                        {isOwnMessage && message.isRead && <span className="read-indicator"> ✓</span>}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <form onSubmit={handleSendMessage} className="message-input-area">
            <div className="message-input-container">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Message ${otherUser.firstName}...`}
                rows="3"
                maxLength="1000"
                disabled={sending}
              />
              <button 
                type="submit" 
                className="send-button"
                disabled={sending || !newMessage.trim()}
              >
                {sending ? 'Sending...' : 'Send'}
              </button>
            </div>
            <div className="character-count">
              {newMessage.length}/1000
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SessionDetailView;